import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { CookiesKey } from '@/app/utils/cookies';

class AdminApiV2Request {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: 'https://back.padelmatchapp.com/api/v2',
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = Cookies.get(CookiesKey.token);
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private handleError(error: any): never {
    // Mirror behavior of app request util
    console.error('API v2 Error:', error);
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 401) {
        Cookies.remove(CookiesKey.token);
        Cookies.remove(CookiesKey.user);
        try {
          const host = window.location.hostname.toLowerCase();
          const isAdminHost = host === 'admin.localhost' || host.startsWith('admin.');
          const isOnLogin = window.location.pathname.startsWith('/login');
          if (isAdminHost && !isOnLogin) {
            const current = `${window.location.pathname}${window.location.search}` || '/courts';
            const redirect = encodeURIComponent(current);
            window.location.href = `/login?redirect=${redirect}`;
          } else if (!isOnLogin) {
            window.location.href = '/';
          }
        } catch {
          window.location.href = '/';
        }
      }
      throw error.response.data;
    }
    throw error;
  }

  async get<T>(url: string, { query, config }: { query?: Record<string, any>; config?: AxiosRequestConfig } = {}): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp: AxiosResponse<T> = await this.client.get(url, { ...config, params: query, headers: { ...(config?.headers || {}), ...headers } });
      return resp.data;
    } catch (e) {
      return this.handleError(e);
    }
  }

  async post<T>(url: string, { data, query, config }: { data?: any; query?: Record<string, any>; config?: AxiosRequestConfig } = {}): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp: AxiosResponse<T> = await this.client.post(url, data, { ...config, params: query, headers: { ...(config?.headers || {}), ...headers } });
      return resp.data;
    } catch (e) {
      return this.handleError(e);
    }
  }

  async put<T>(url: string, { data, query, config }: { data?: any; query?: Record<string, any>; config?: AxiosRequestConfig } = {}): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp: AxiosResponse<T> = await this.client.put(url, data, { ...config, params: query, headers: { ...(config?.headers || {}), ...headers } });
      return resp.data;
    } catch (e) {
      return this.handleError(e);
    }
  }

  async patch<T>(url: string, { data, query, config }: { data?: any; query?: Record<string, any>; config?: AxiosRequestConfig } = {}): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp: AxiosResponse<T> = await this.client.patch(url, data, { ...config, params: query, headers: { ...(config?.headers || {}), ...headers } });
      return resp.data;
    } catch (e) {
      return this.handleError(e);
    }
  }

  async delete<T>(url: string, { query, config }: { query?: Record<string, any>; config?: AxiosRequestConfig } = {}): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp: AxiosResponse<T> = await this.client.delete(url, { ...config, params: query, headers: { ...(config?.headers || {}), ...headers } });
      return resp.data;
    } catch (e) {
      return this.handleError(e);
    }
  }
}

const requestV2 = new AdminApiV2Request();
export default requestV2;
